import { useMemo, useState } from "react"
import { useDebouncedCallback } from "use-debounce"

import { Badge, Empty, Select, type SelectProps, Spin, Text } from "@/atoms"
import { useTranslation } from "@/hooks/useTranslation"
import { useProjects } from "@/organisms/Drawers/applications/inventory/useInventory"
import { Form } from "@/organisms/Form"

import styles from "./ProjectSelect.module.scss"

export const ProjectSelect = (props: SelectProps) => {
  const { onChange, ...restProps } = props
  const [searchValue, setSearchValue] = useState("")
  const projectsCtx = useProjects(
    searchValue ? { projectName: searchValue } : {}
  )
  const { form } = Form.useForm()
  const onSearch = useDebouncedCallback((value) => {
    setSearchValue(value)
  }, 300)
  const { t: tConfiguration } = useTranslation("translation", {
    keyPrefix: "inventory.vm.drawer.configuration"
  })

  const options = useMemo(() => {
    return projectsCtx.data?.pages.map((project) => {
      return {
        label: project.projectName,
        value: `${project.codeRepoId}__${project.paths.join("")}`,
        isCorrelatedToImage: project.correlations?.some(
          (c) => c.artifactType === "ARTIFACT_TYPE_IMAGE"
        ),
        data: project
      }
    })
  }, [projectsCtx.data?.pages])

  return (
    <Select
      loading={projectsCtx.isLoading}
      showSearch={{
        onSearch,
        autoClearSearchValue: false,
        filterOption: false
      }}
      options={options}
      optionRender={(option: unknown) => {
        const { label, data } = option as {
          label: string
          data: { isCorrelatedToImage: boolean }
        }
        const { isCorrelatedToImage } = data || {}

        return (
          <div className={styles.option}>
            <span className={styles.optionLabel}>{label}</span>
            {isCorrelatedToImage && (
              <Badge
                dot
                color="#1890FF"
                text={
                  <Text type="secondary" className="sm-text-ks">
                    {tConfiguration("correlatedImages")}
                  </Text>
                }
              />
            )}
          </div>
        )
      }}
      notFoundContent={
        projectsCtx.isFetchingNextPage || projectsCtx.isLoading ? (
          <Spin size="small" />
        ) : (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )
      }
      popupRender={(menu) => (
        <>
          {menu}
          {projectsCtx.isFetchingNextPage && (
            <div className={styles.spinOption}>
              <Spin size="small" />
            </div>
          )}
        </>
      )}
      onPopupScroll={(e) => {
        const target = e.target as HTMLElement
        const { scrollTop, offsetHeight, scrollHeight } = target

        if (
          projectsCtx.hasNextPage &&
          scrollTop + offsetHeight >= scrollHeight - 32
        )
          projectsCtx.fetchNextPage()
      }}
      onChange={(value, option) => {
        onChange?.(value)
        form.setFieldValue("project", option)
      }}
      {...restProps}
    />
  )
}
